<template>
  <div class="fill-form-page">
    <div v-if="formData.logoUrl" class="form-logo text-center mb-2 d-flex flex-column align-items-center">
      <img :src="formData.logoUrl" alt="Company Logo" style="max-height: 60px; max-width: 180px; object-fit: contain;" />
      <b-button size="sm" variant="outline-danger" class="mt-1 w-auto" @click="formData.logoUrl = ''">Remove</b-button>
    </div>
    <b-card class="mb-2">
      <h3 class="text-center mb-2">{{ formData.title || 'Untitled Form' }}</h3>
      <p class="text-center text-muted mb-0">{{ formData.description || 'Form description' }}</p>
    </b-card>
    <b-card class="form-card">
      <b-form @submit.prevent="submitForm">
        <div v-for="(question, idx) in formData.questions" :key="question.id" class="mb-4">
          <template v-if="question.type === 'header'">
            <component :is="question.headerStyle || 'h2'" :class="`text-${question.headerColor || 'primary'} mb-2`">
              {{ question.text || 'Header Text' }}
            </component>
          </template>
          <template v-else>
            <b-form-group :label-for="'q-' + question.id" label-class="question-label">
              <template #label>
                <span v-html="renderLabel(question)"></span>
              </template>
              <!-- MCQ -->
              <b-form-select v-if="question.type === 'mcq'" v-model="answers[question.id]" :options="question.options.map((opt, i) => ({ value: opt, text: opt }))" :state="null" :placeholder="'Select an option'" />
              <!-- Radio -->
              <div v-else-if="question.type === 'radio'">
                <b-form-radio-group v-model="answers[question.id]" class="d-flex flex-wrap align-items-center">
                  <b-form-radio v-for="(opt, i) in question.options" :key="i" :value="opt" class="mr-3 mb-1">{{ opt }}</b-form-radio>
                </b-form-radio-group>
                <b-form-textarea
                  v-if="showRadioConditional(question, answers[question.id])"
                  v-model="answers[question.id + '_conditional']"
                  :placeholder="getRadioConditionalPlaceholder(question, answers[question.id])"
                  rows="2"
                  class="mt-2"
                />
              </div>
              <!-- Checkbox -->
              <b-form-checkbox-group v-else-if="question.type === 'checkbox'" v-model="answers[question.id]" class="d-flex flex-wrap align-items-center">
                <b-form-checkbox v-for="(opt, i) in question.options" :key="i" :value="opt" class="mr-3 mb-1">{{ opt }}</b-form-checkbox>
              </b-form-checkbox-group>
              <!-- One Liner -->
              <b-form-input v-else-if="question.type === 'one-liner'" v-model="answers[question.id]" :placeholder="question.placeholder || 'Enter your answer'" />
              <!-- Fill in the Blanks -->
              <b-form-textarea v-else-if="question.type === 'fill-blanks'" v-model="answers[question.id]" :placeholder="'Fill in the blanks'" rows="3" />
              <!-- Brief Answer -->
              <b-form-textarea v-else-if="question.type === 'brief'" v-model="answers[question.id]" :placeholder="'Enter your answer'" rows="4" />
            </b-form-group>
          </template>
        </div>
        <div class="text-center mt-4">
          <b-button type="submit" variant="success" size="lg" class="submit-btn w-100">Submit</b-button>
        </div>
      </b-form>
      <b-modal v-model="showSuccess" title="Thank you!" hide-footer centered>
        <div class="text-center py-3">
          <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-2" />
          <h5>Your response has been submitted successfully!</h5>
          <p class="text-muted">You will be redirected back to the forms page.</p>
        </div>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormRadioGroup, BFormRadio, BFormCheckboxGroup, BFormCheckbox, BFormTextarea, BButton, BModal } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import FormsService from '@/services/forms-service'

export default {
  name: 'FillForm',
  components: { BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormRadioGroup, BFormRadio, BFormCheckboxGroup, BFormCheckbox, BFormTextarea, BButton, BModal, FeatherIcon },
  data() {
    return {
      formData: { questions: [] },
      answers: {},
      showSuccess: false,
    }
  },
  methods: {
    async loadForm() {
      try {
        const response = await FormsService.getFormById(this.$route.params.id)
        if (response.success && response.data) {
          this.formData = response.data
          // Initialize answers
          for (const q of this.formData.questions) {
            if (q.type !== 'header') {
              this.$set(this.answers, q.id, q.type === 'checkbox' ? [] : '')
            }
          }
        } else {
          throw new Error('Form not found')
        }
      } catch (error) {
        this.$bvToast.toast('Form not found', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        this.$router.push({ name: 'tools-forms-list' })
      }
    },
    renderLabel(question) {
      // Render question text with required asterisk inline
      return question.text + (question.required ? ' <span class="text-danger">*</span>' : '')
    },
    showRadioConditional(question, selected) {
      if (!question.conditionalTexts) return false
      const idx = question.options.indexOf(selected)
      return question.conditionalTexts[idx] && question.conditionalTexts[idx].show
    },
    getRadioConditionalPlaceholder(question, selected) {
      if (!question.conditionalTexts) return ''
      const idx = question.options.indexOf(selected)
      return question.conditionalTexts[idx] && question.conditionalTexts[idx].text || 'Please specify'
    },
    async submitForm() {
      // Validate required
      for (const q of this.formData.questions) {
        if (q.type !== 'header' && q.required) {
          const val = this.answers[q.id]
          if (q.type === 'checkbox' && (!val || val.length === 0)) {
            this.$bvToast.toast('Please answer all required questions.', { title: 'Validation', variant: 'danger', solid: true })
            return
          }
          if ((q.type !== 'checkbox') && (!val || val === '')) {
            this.$bvToast.toast('Please answer all required questions.', { title: 'Validation', variant: 'danger', solid: true })
            return
          }
        }
      }

      // Prepare response data with proper handling of "No" responses
      const responseData = { ...this.answers }
      // Process radio questions to capture "No" responses properly
      for (const q of this.formData.questions) {
        if (q.type === 'radio' && responseData[q.id] && responseData[q.id].toLowerCase() === 'no') {
          const noReasonKey = q.id + '_no_reason'
          if (responseData[noReasonKey]) {
            // Store the "No" reason in a structured way
            responseData[q.id] = {
              answer: 'No',
              reason: responseData[noReasonKey]
            }
            // Remove the separate key
            delete responseData[noReasonKey]
          }
        }
      }
      // Add conditional text to responseData BEFORE saving
      for (const q of this.formData.questions) {
        if (q.type === 'radio' && this.showRadioConditional(q, this.answers[q.id])) {
          responseData[q.id + '_conditional'] = this.answers[q.id + '_conditional'] || ''
        }
      }

      try {
        // Submit response to backend
        const newResponse = {
          formId: this.formData.id,
          answers: responseData,
          submittedAt: new Date().toISOString(),
          userId: this.getCurrentUserId() // Add user identification
        }
        
        await FormsService.submitFormResponse(this.formData.id, newResponse)

        // Show success modal
        this.showSuccess = true

        // Redirect after 2 seconds
        setTimeout(() => {
          this.$router.push({ name: 'tools-forms-view-submission', params: { id: this.formData.id } })
        }, 2000)
      } catch (error) {
        this.$bvToast.toast('Error submitting form', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    getCurrentUserId() {
      // Get current user ID from localStorage or generate a temporary one
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      return userData.id || 'anonymous_' + Date.now()
    }
  },
  async mounted() {
    await this.loadForm()
  },
}
</script>

<style scoped>
:root {
  --form-bg: #fff;
  --form-text: #222;
  --form-border: #ebe9f1;
}
@media (prefers-color-scheme: dark) {
  :root {
    --form-bg: #181824;
    --form-text: #f1f1f1;
    --form-border: #33344a;
  }
}
.fill-form-page, .b-card, .b-form-group {
  background: var(--form-bg) !important;
  color: var(--form-text) !important;
  border-color: var(--form-border) !important;
}
.b-form-input, .b-form-select, .b-form-textarea {
  font-size: 0.98rem;
  padding: 6px 10px;
}
.b-form-group, .form-card {
  margin-bottom: 8px !important;
}
.b-card {
  padding: 12px 14px !important;
}
.fill-form-page {
  max-width: 700px;
  margin: 0 auto;
  padding: 16px;
  background: #fff;
  border-radius: 18px;
}

.form-logo {
  margin-bottom: 18px;
}

.form-card {
  border-radius: 16px;
  border: 1.5px solid #ebe9f1;
  padding: 32px 36px 22px 36px;
  background: #fff;
}

.question-label {
  font-size: 1.15rem;
  color: #5e5873;
  font-weight: 700;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.b-form-input,
.b-form-select,
.b-form-textarea {
  border-radius: 8px !important;
  border: 1.5px solid #d6d8dd !important;
  font-size: 1.08rem;
  padding: 10px 14px;
  background: #f8f8fa;
  transition: border-color 0.2s;
}
.b-form-input:focus,
.b-form-select:focus,
.b-form-textarea:focus {
  border-color: #7367f0 !important;
  background: #fff;
  /* box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.08); */
}

.b-form-radio,
.b-form-checkbox {
  margin-right: 1.2rem !important;
  margin-bottom: 0.5rem !important;
}

.submit-btn {
  font-weight: 700;
  border-radius: 10px;
  padding: 12px 0;
  font-size: 1.13rem;
  background: linear-gradient(90deg, #7367f0 0%, #9f8cff 100%);
  border: none;
  transition: background 0.2s;
}
.submit-btn:hover, .submit-btn:focus {
  background: linear-gradient(90deg, #5f50e6 0%, #8a7be3 100%);
}

.b-card {
  border-radius: 16px !important;
}

@media (max-width: 767px) {
  .fill-form-page {
    max-width: 100%;
    padding: 4px;
    border-radius: 10px;
  }
  .form-card {
    padding: 12px 6px 10px 6px;
    border-radius: 10px;
  }
  .submit-btn {
    width: 100% !important;
    font-size: 1.08rem;
  }
  .question-label {
    font-size: 1.01rem;
  }
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .fill-form-page {
    padding: 0.5rem;
  }
  
  .card {
    margin-bottom: 1rem;
  }
  
  .card-header {
    padding: 0.75rem;
  }
  
  .card-body {
    padding: 0.75rem;
  }
  
  .form-card {
    padding: 1.5rem;
  }
  
  .btn {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
  }
  
  .form-label {
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .form-control {
    font-size: 0.9rem;
  }
  
  .b-form-radio,
  .b-form-checkbox {
    margin-right: 0.5rem !important;
    margin-bottom: 0.25rem !important;
  }
  
  .question-label {
    font-size: 1rem;
  }
  
  .b-form-input,
  .b-form-select,
  .b-form-textarea {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 576px) {
  .fill-form-page {
    padding: 0.25rem;
  }
  
  .card-header {
    padding: 0.5rem;
  }
  
  .card-body {
    padding: 0.5rem;
  }
  
  .form-card {
    padding: 1rem;
  }
  
  .btn {
    font-size: 0.8rem;
    padding: 0.375rem 0.75rem;
  }
  
  .form-label {
    font-size: 0.8rem;
  }
  
  .form-control {
    font-size: 0.8rem;
  }
  
  .question-label {
    font-size: 0.9rem;
  }
  
  .b-form-input,
  .b-form-select,
  .b-form-textarea {
    font-size: 0.8rem;
    padding: 0.375rem 0.5rem;
  }
  
  .b-form-radio,
  .b-form-checkbox {
    margin-right: 0.25rem !important;
    margin-bottom: 0.125rem !important;
  }
  
  .submit-btn {
    font-size: 0.9rem;
    padding: 0.75rem 0;
  }
}
</style> 